import React, { useState } from 'react';
import { useFormikContext } from 'formik';

import { SortableTable } from '~/components/core';
import AlertBanner from '~/components/core/AlertBanner';
import Button from '~/components/core/Atomic/Buttons/Button';
import Text from '~/components/core/TextComponents/Text';
import { serverDateFormatWithoutTZChange } from '~/DateTimeUtils';
import type {
  SlaRulePreviewResult,
  SlaRulePreviewResultSlaPreviewSampleRow,
  SlaRulePreviewResultSlaThresholdPreviewResult,
} from '~/server_shared/openapi/data-contracts';

import { previewSlaRule } from '../api/slaRulesApi';
import { TEXTS } from '../texts';

import type { SlaRuleFormValues } from './formValues';
import { previewKey, toPreviewRequest } from './formValues';

interface PreviewPanelProps {
  organizationId: number;
  ruleId: number | null;
  canPreview: boolean;
  /** A claim-level card's rows name no exposure, so its sample has no exposure column. */
  isClaimLevel: boolean;
}

interface StoredPreview {
  /** `previewKey` of the values the request carried. */
  key: string;
  result: SlaRulePreviewResult;
}

/** SortableTable keys its rows by `id`, which a sample row does not carry. */
type SampleRow = SlaRulePreviewResultSlaPreviewSampleRow & { id: number };

interface SampleColumn {
  id: keyof SlaRulePreviewResultSlaPreviewSampleRow;
  label: string;
  numeric?: boolean;
  isHidden?: boolean;
  specialCell?: (row: SampleRow) => React.ReactNode;
}

const ThresholdPreview: React.FC<{
  threshold: SlaRulePreviewResultSlaThresholdPreviewResult;
  daysUnit: string;
  isClaimLevel: boolean;
}> = ({ threshold, daysUnit, isClaimLevel }) => {
  const sampleRows: SampleRow[] = (threshold.sample_rows ?? []).map((row, position) => ({ ...row, id: position }));

  const columns: SampleColumn[] = [
    {
      id: 'claim_id',
      label: TEXTS.PREVIEW_COLUMN_CLAIM,
      // Sorted by id, which is the order the sample arrives in (oldest claim first).
      numeric: true,
      specialCell: (row) => row.claim_id_display ?? row.claim_id,
    },
    { id: 'exposure_id', label: TEXTS.PREVIEW_COLUMN_EXPOSURE, numeric: true, isHidden: isClaimLevel },
    {
      id: 'anchor_date',
      label: TEXTS.PREVIEW_COLUMN_ANCHOR_DATE,
      specialCell: (row) => serverDateFormatWithoutTZChange(row.anchor_date),
    },
    { id: 'days_elapsed', label: TEXTS.PREVIEW_COLUMN_DAYS_ELAPSED, numeric: true },
  ];

  return (
    <div className="flex flex-col gap-8">
      <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.SEMI_BOLD}>
        {TEXTS.PREVIEW_THRESHOLD(threshold.sla_days, threshold.repeat_every_days, threshold.would_fire_count, daysUnit)}
      </Text>
      {/* The rows are a capped sample, narrowed to the claims this admin may open; the count is org-wide. */}
      {sampleRows.length < threshold.would_fire_count && (
        <Text variant={Text.VARIANTS.XS} weight={Text.WEIGHTS.REGULAR} colorVariant={Text.COLOR_VARIANTS.SECONDARY}>
          {TEXTS.PREVIEW_SAMPLE_NOTE}
        </Text>
      )}
      {sampleRows.length > 0 && <SortableTable columns={columns} rows={sampleRows} />}
    </div>
  );
};

const PreviewResult: React.FC<{ result: SlaRulePreviewResult; daysUnit: string; isClaimLevel: boolean }> = ({
  result,
  daysUnit,
  isClaimLevel,
}) => (
  <div className="flex flex-col gap-12">
    <Text variant={Text.VARIANTS.XS} weight={Text.WEIGHTS.REGULAR} colorVariant={Text.COLOR_VARIANTS.SECONDARY}>
      {TEXTS.PREVIEW_AS_OF(serverDateFormatWithoutTZChange(result.snapshot_date))}
    </Text>
    {/* The counts still follow: they are how the admin sees why the rule is over the cap. */}
    {result.is_over_entity_cap && (
      <AlertBanner
        alertType={AlertBanner.ALERT_TYPES.WARNING}
        note={TEXTS.PREVIEW_OVER_CAP(result.matched_entity_count, result.entity_cap)}
        withIcon
      />
    )}
    {(result.thresholds ?? []).map((threshold) => (
      // A schedule is unique within a rule: the preview route refuses a duplicate.
      <ThresholdPreview
        key={`${threshold.sla_days}/${threshold.repeat_every_days}`}
        threshold={threshold}
        daysUnit={daysUnit}
        isClaimLevel={isClaimLevel}
      />
    ))}
  </div>
);

const PreviewPanel: React.FC<PreviewPanelProps> = ({ organizationId, ruleId, canPreview, isClaimLevel }) => {
  const { values } = useFormikContext<SlaRuleFormValues>();
  const [isLoading, setIsLoading] = useState(false);
  const [preview, setPreview] = useState<StoredPreview | null>(null);

  const handlePreview = async () => {
    // Keyed on what is sent, so an edit made while the request is in flight leaves its answer stale.
    const key = previewKey(values);
    setIsLoading(true);
    try {
      setPreview({ key, result: await previewSlaRule(organizationId, toPreviewRequest(values, ruleId)) });
    } catch {
      /* already surfaced via reportAxiosError */
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex flex-col gap-12">
      <div>
        <Button color="primary" variant="outlined" disabled={isLoading || !canPreview} onClick={handlePreview}>
          {TEXTS.PREVIEW_BUTTON}
        </Button>
      </div>
      {preview &&
        (preview.key === previewKey(values) ? (
          // The result does not echo the day count's unit; the key covers `is_business_days`, so the
          // form's toggle is the one this result was computed with.
          <PreviewResult
            result={preview.result}
            daysUnit={TEXTS.DAYS_UNIT(values.is_business_days)}
            isClaimLevel={isClaimLevel}
          />
        ) : (
          <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.REGULAR} colorVariant={Text.COLOR_VARIANTS.SECONDARY}>
            {TEXTS.PREVIEW_STALE}
          </Text>
        ))}
    </div>
  );
};

export default PreviewPanel;
